/**
 * =========================================================================
 * THE RECOMPUTE LANE (P21) — the dead end, turned into an answer
 * =========================================================================
 * Before these panels a ticket in `NeedsReview(CustomerAmbiguous)` could only
 * be looked at. "Compose again" was refused by the gate (`AuthorizationStatus`
 * is `NotFound`, and only step 7 moves it — 28 of 35 live review tickets sat
 * behind exactly that), "Reprocess" set a field nobody reads, and changing the
 * status by hand cleared the reason while leaving the ticket with no customer
 * at all.
 *
 * Setting the fact here re-runs steps 5, 7, 8 and 9 on the server. The panels
 * say so, because "it is not just a label" is the thing a user has to know
 * before they click.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCustomerCandidates, useProductCandidates, usePurchasedFamilies } from '../hooks/useTicketsApi';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Confidence } from './ui/Confidence';
import { Input, Select } from './ui/Field';
import { FOCUS_RING } from './ui/focus';
import {
  DUPLICATE_ACTIONS, DUPLICATE_LABELS, MATCH_BAND_TONE, MATCH_BAND_LABELS, REVIEW_REASON_LABELS,
  TICKET_STATUS_LABELS, companyLabel, companyTooltip, familyOfReason, labelFor, tooltipFor,
} from '../lib/labels';
import type {
  CandidateListMeta, CandidateSuggestion, CustomerCandidate, FactEditConsequences, IngestExplanation,
  MatchBand, ProductCandidate, PurchasedFamily, Ticket,
} from '../types/domain';

/**
 * =========================================================================
 * A SETTLED FACT IS EDITABLE (P28) — WHAT THAT CHANGES IN THESE PANELS
 * =========================================================================
 * Until now every panel here appeared only while a fact was MISSING, and
 * disappeared the moment it was answered. Live on 2026-09-02 that left 26 open
 * tickets carrying a customer and an article that nobody could correct, and one
 * of them — `DRA-2026-00044` — was a genuine dead end: the customer had been
 * set in the wrong company, so the article could not be found in it, and the
 * only control on the screen searched the wrong company's catalogue.
 *
 * The same panels now open in EDIT mode as well. Two things differ, and both
 * are about honesty rather than layout:
 *
 *   - The panel names the CURRENT value. Changing something starts by seeing
 *     what it is.
 *   - The panel states what the change would COST, from the server's own count
 *     of waiting drafts and open Sales requests, before anybody clicks.
 */

/** The panel is answering an open question, or changing a settled fact. */
export type FactPanelMode = 'answer' | 'edit';

/**
 * A pick, carried upward together with what the server said it would cost.
 *
 * The consequences travel WITH the choice rather than being read again by the
 * modal: they were fetched in the same response as the row that was clicked, so
 * the sentence in front of the person describes the same moment as the list
 * they picked from.
 */
export interface FactChoice<T> {
  candidate: T;
  consequences: FactEditConsequences;
  isEdit: boolean;
}

/** The value that means "search both pilot companies". Mirrors `customer-candidates.ts`. */
export const BOTH_COMPANIES = 'both';

/**
 * The cap on one multi-pick. Mirrors `MAX_SPLIT_TICKETS` in
 * `agent/steps/split-products.ts`, where it is enforced — this one only stops
 * the button before the server has to refuse.
 */
export const MAX_PICKED_PRODUCTS = 40;

/**
 * The sentence a ConfirmModal adds when the edit is going to withdraw something.
 *
 * Empty when there is nothing to say — a first answer on a ticket with no
 * artefacts costs nothing, and inventing a warning for it would teach people to
 * click through the ones that matter.
 */
export function consequenceSentence(c: FactEditConsequences | undefined | null): string {
  if (!c) return '';
  const parts: string[] = [];
  if (c.drafts.length > 0) {
    const ids = c.drafts.map(d => `${d.draftId}`).join(', ');
    const edited = c.drafts.some(d => d.editedBeforeSend);
    parts.push(
      `Draft ${ids} waiting in Approvals ${c.drafts.length === 1 ? 'is' : 'are'} withdrawn and the reply is `
      + 'written again from the corrected facts'
      + (edited
        ? ' — including one whose wording was edited by hand, and that text is NOT carried over'
        : ''),
    );
  }
  if (c.authorizationRequest) {
    parts.push(
      `the open authorization request to ${c.authorizationRequest.requestedFrom ?? 'a Sales Manager'} is cancelled `
      + '(they are not mailed; the request simply leaves their dashboard) and the purchase check is asked again',
    );
  }
  if (c.statusResetFrom) {
    parts.push(`the ticket returns from ${c.statusResetFrom} to New`);
  }
  if (parts.length === 0) return '';
  return `${parts.join('; ')}. Nothing is sent, and nothing is deleted.`;
}

/**
 * The caption above a candidate list, and the agent's older sentence beneath it.
 *
 * Two paragraphs and never one: the first describes the list on the screen, the
 * second is dated history. Merging them is what put a `dch` explanation over a
 * `dit` list on `DRA-2026-00044`.
 */
function CandidateListCaption({ meta, explanation, ingest }: {
  meta: CandidateListMeta | undefined;
  explanation: string | null | undefined;
  ingest: IngestExplanation | null | undefined;
}) {
  if (!explanation && !ingest) return null;
  return (
    <>
      {explanation && (
        <p className="mt-0.5 text-xs text-amber-900/70" title={meta ? `Source: ${meta.source}` : undefined}>
          {explanation}
        </p>
      )}
      {ingest && (
        <p className="mt-0.5 text-xs italic text-amber-900/55">
          At ingest{ingest.occurredAt ? ` on ${ingest.occurredAt.slice(0, 10)}` : ''} the agent said:{' '}
          {ingest.explanation}
        </p>
      )}
    </>
  );
}

/** The current value of a fact, above the control that changes it. */
function CurrentValue({ label, value, raw }: { label: string; value: string | null; raw?: string | null }) {
  return (
    <div className="mt-2 rounded border border-amber-200 bg-white px-3 py-2 text-xs">
      <span className="text-gray-500">{label}: </span>
      <span className="font-medium text-gray-900">{value ?? 'not set'}</span>
      {raw && <span className="ml-2 text-gray-500">· the mail said &ldquo;{raw}&rdquo;</span>}
    </div>
  );
}

/**
 * THE MATCH SCORE, AS A BADGE (P25).
 *
 * Both the number and the band arrive from the server. This component decides
 * nothing — it colours what it was told, and shows nothing at all when there is
 * no score, which is the case for a candidate matched on the sender's domain
 * rather than on a name.
 *
 * The percentage on screen is the MATCH CONFIDENCE and never the model's
 * extraction confidence. They are different numbers about different things, and
 * the ticket screen already shows the model's one, labelled as such, in the
 * language panel.
 */
export function MatchScoreBadge({ score, band }: { score: number | null; band: MatchBand | null }) {
  if (score === null || band === null) return null;
  return (
    // P38: the kit's `Badge` with a tone from `labels.ts`, where it used to carry
    // its own `green-50 / amber-50 / red-50` plus a `ring` at 20 % alpha that nothing else
    // in the app uses. 11 px → 12 px with it (task 6). No glyph: the NUMBER is
    // what does not work alone, and the number is right there.
    <Badge
      tone={MATCH_BAND_TONE[band]}
      className="ml-1.5"
      title={`${labelFor(MATCH_BAND_LABELS, band)} — ${tooltipFor(MATCH_BAND_LABELS, band)}`}
    >
      {score}%
    </Badge>
  );
}

/**
 * One scope in the company toggle (P39).
 *
 * It measured 33 × 26 px in a pair of hand-mixed ambers — under §4's 32 px floor
 * and in a selected state nothing else in the app uses. It is NOT the kit's
 * primary: §7 allows one primary per view, this screen's is the reply, and a
 * chosen filter is not an action. It follows §7's filter-chip rule instead —
 * applied = ink ground — and says which is on with `aria-pressed`, because a
 * ground colour is not something a screen reader can report.
 *
 * The WORD is `companyLabel`'s (K4: CH / IT), with the full company name and the
 * ERP code in the tooltip, so this panel names a company the way the board does.
 */
function CompanyScopeButton({ code, selected, onSelect }: {
  code: string; selected: boolean; onSelect: () => void;
}) {
  const both = code === BOTH_COMPANIES;
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={both ? 'Search both Dolder companies at once.' : companyTooltip(code)}
      onClick={onSelect}
      className={`inline-flex h-8 items-center rounded-ctl px-2.5 text-xs font-medium ${FOCUS_RING} ${
        selected ? 'bg-ink text-white' : 'border border-hair bg-surface text-body hover:bg-hover'
      }`}
    >
      {both ? 'Both' : companyLabel(code)}
    </button>
  );
}

/** Is this the one row the SERVER said may be preselected? */
function isSuggested(suggestion: CandidateSuggestion | null | undefined, erpId: string, companyCode: string): boolean {
  return !!suggestion && suggestion.erpId === erpId && suggestion.companyCode === companyCode;
}

/**
 * "Which customer is this?"
 *
 * TWO LISTS, KEPT APART ON PURPOSE.
 * `agentCandidates` is what the pipeline recorded. It is genuinely useful when
 * the agent was undecided between two real customers — `DRA-2026-00043` names
 * Frike in `dch` and Frike in `dit`. It is actively misleading for every ticket
 * a colleague forwarded before 2026-08-30, where it is the six Dolder group
 * companies that happen to carry a `@dolder.com` address. Merging the lists
 * would hide which is which, so they are labelled and separate — and the search
 * box, not the stored list, is the thing that always works.
 *
 * The COMPANY is on every row and never derived. Eleven pilot customer names
 * exist in both companies, and the company decides which Sales Manager is asked
 * and which article a product name means.
 */
export function SetCustomerPanel({
  ticketNumber, rawText, mode = 'answer', currentName, pending, onChoose, onCancel,
}: {
  ticketNumber: string;
  rawText: string | null;
  mode?: FactPanelMode;
  /** The customer's name as the header card shows it — the id alone is not a value a person recognises. */
  currentName?: string | null;
  pending: boolean;
  onChoose: (choice: FactChoice<CustomerCandidate>) => void;
  onCancel?: () => void;
}) {
  const [term, setTerm] = useState('');
  // '' means "let the server decide" — the ticket's own company (decision 7).
  // The selector then displays what the server actually used.
  const [company, setCompany] = useState('');
  const { data, isLoading } = useCustomerCandidates(ticketNumber, term.trim(), true, company || undefined);

  const agent = data?.agentCandidates ?? [];
  const results = data?.searchResults ?? [];
  const effectiveTerm = data?.searchTerm ?? '';
  const suggestion = data?.suggestion ?? null;
  const consequences = data?.consequences;
  const editing = mode === 'edit';
  const pick = (c: CustomerCandidate) => onChoose({
    candidate: c,
    consequences: consequences ?? { drafts: [], authorizationRequest: null, sentReply: null, statusResetFrom: null },
    isEdit: data?.isEdit ?? editing,
  });
  // The selector's value: the explicit choice, else the company the server used,
  // else "both" — which is what a ticket with no company at all really gets.
  const shownCompany = company || data?.companyFilter || BOTH_COMPANIES;
  const companyOptions = data?.scope.companyCodes ?? [];

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold text-warn-ink">
          {editing ? 'Change the customer' : 'Which customer is this?'}
        </h2>
        {/* P39: 36 × 16 px before. The kit's quiet button, like the Cancel on the
            fact card it mirrors — same height, same focus ring, same 32 px floor. */}
        {onCancel && (
          <Button variant="quiet" size="sm" className="shrink-0 underline hover:no-underline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <p className="mt-1 text-amber-900/90">
        The mail said <strong>{rawText || 'nothing the agent could use'}</strong>.{' '}
        {editing
          ? 'Changing the customer here re-runs the duplicate check, the purchase check and the reply draft — and '
            + 'withdraws whatever the previous customer produced.'
          : 'Setting the customer here re-runs the duplicate check, the purchase check and the reply draft on this '
            + 'ticket — it is not just a label.'}
      </p>

      {editing && <CurrentValue label="Currently" value={currentName ?? null} raw={rawText} />}

      {consequenceSentence(consequences) && (
        <p className="mt-2 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
          If you change it: {consequenceSentence(consequences)}
        </p>
      )}

      {/* Roman's decision 7 — the company is chosen BEFORE the search, because
          eleven pilot customer names exist in both companies and the company
          decides which Sales Manager is asked and which article a product name
          means. "Both" stays one click away: seeing the two rows side by side is
          how the two-company case is decided at all. */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-amber-900/70">Company</span>
        {[...companyOptions, BOTH_COMPANIES].map(code => (
          <CompanyScopeButton
            key={code}
            code={code}
            selected={shownCompany === code}
            onSelect={() => setCompany(code)}
          />
        ))}
      </div>

      <Input
        type="search"
        value={term}
        placeholder={rawText ? `Search customers — e.g. "${rawText}"` : 'Search customers by name'}
        onChange={e => setTerm(e.target.value)}
        className="mt-3"
      />

      {agent.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-amber-900/70">
            What the agent considered
          </div>
          <CandidateListCaption
            meta={data?.agentList}
            explanation={data?.agentExplanation}
            ingest={data?.agentIngest}
          />
          <ul className="mt-1 space-y-1">
            {agent.map(c => (
              <CandidateRow
                key={`agent-${c.companyCode}-${c.customerId}`}
                name={c.customerName}
                id={c.customerId}
                companyCode={c.companyCode}
                score={c.score}
                band={c.band}
                suggested={isSuggested(suggestion, c.customerId, c.companyCode)}
                disabled={pending}
                onPick={() => pick(c)}
              />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3">
        <div className="text-xs font-medium uppercase tracking-wide text-amber-900/70">
          Search{effectiveTerm ? <span className="normal-case"> — {effectiveTerm}</span> : null}
        </div>
        {isLoading && <div className="mt-1 text-xs text-amber-900/60">Searching…</div>}
        {!isLoading && results.length === 0 && (
          <div className="mt-1 text-xs text-amber-900/60">
            No customer in {data?.scope.companyCodes.join('/') ?? 'the pilot scope'}
            {data?.scope.businessUnitCode ? ` (${data.scope.businessUnitCode})` : ''} is even close. The match
            ignores spaces, dots and dashes and tolerates a typo — &quot;Mapei Spa&quot; finds
            &quot;MAPEI S.P.A.&quot;, and &quot;Mapay&quot; still offers it. If nothing comes back at all, the
            customer is probably outside the pilot scope.
          </div>
        )}
        <ul className="mt-1 space-y-1">
          {results.map(c => (
            <CandidateRow
              key={`search-${c.companyCode}-${c.customerId}`}
              name={c.customerName}
              id={c.customerId}
              companyCode={c.companyCode}
              score={c.score}
              band={c.band}
              suggested={isSuggested(suggestion, c.customerId, c.companyCode)}
              note={c.via === 'Fuzzy' ? 'close, not exact' : undefined}
              disabled={pending}
              onPick={() => pick(c)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * "Which article is this?"
 *
 * FAMILIES, not packaging variants: a safety data sheet is per substance, and
 * every agent-created ticket carries a master id (P6 measured 20311 of 20569
 * pilot transaction lines sitting on variants). The row says how many variants
 * a family covers rather than pretending the drum size matters.
 *
 * With no company on the ticket the search cannot run at all, and the panel
 * says so instead of showing an empty list: the same product name means
 * different articles in `dit` and `dch`, so the customer is settled first.
 */
/**
 * "WHICH OF THEIR PRODUCTS?" — the fan-out picker (P26, Roman's decision 4).
 *
 * =========================================================================
 * WHY THIS IS NOT THE SEARCH PANEL WITH CHECKBOXES
 * =========================================================================
 * `SetProductPanel` searches the whole catalogue and settles ONE article. This
 * one lists a fact instead of a search: what this customer has actually bought
 * in the lookback, family by family, samples and open orders included — the same
 * D13 definition the authorization ladder uses, so nothing offered here can then
 * fail the ladder for lack of a purchase.
 *
 * Every tick becomes a TICKET, and the panel says so before the click rather
 * than after it. That is the whole weight of the decision: the pilot answers
 * each ticket with its own mail (bundling is on the Phase-3 list), so ticking
 * twelve boxes is telling a customer they will receive twelve mails.
 *
 * The screen sends article IDS and nothing else. The server re-reads the
 * purchase history and takes the name, the company and the family from its own
 * result — the same rule the draft attachments follow since P24.
 */
export function SplitProductsPanel({
  ticketNumber, pending, onSplit,
}: {
  ticketNumber: string;
  pending: boolean;
  onSplit: (productIds: string[], families: PurchasedFamily[]) => void;
}) {
  const { data, isLoading, isError } = usePurchasedFamilies(ticketNumber, true);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const families = data?.families ?? [];
  const available = families.filter(f => f.alreadySplitAs === null);
  const chosen = available.filter(f => picked.has(f.productId));
  const overLimit = data ? chosen.length > data.maxPerSplit : false;

  function toggle(productId: string) {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <h2 className="text-sm font-semibold text-warn-ink">Which of their products?</h2>
      <p className="mt-1 text-amber-900/90">
        The mail asks for the documents of everything this customer buys, so there is no single article to look up.
        Below is what they have actually bought
        {data?.lookbackMonths ? ` in the last ${data.lookbackMonths} months` : ''} — one row per substance, not per
        pack size{data?.samplesCountAsPurchase ? ', samples and outstanding orders included' : ''}.
        <strong> Each row you tick becomes its own ticket</strong>, and each of those is answered with its own mail.
      </p>

      {data && !data.splittable && (
        <div className="mt-3 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
          {data.notSplittableReason}
        </div>
      )}

      {isLoading && <div className="mt-3 text-xs text-amber-900/60">Reading the purchase history…</div>}
      {isError && (
        <div className="mt-3 text-xs text-red-700">The purchase history could not be read. Try again in a moment.</div>
      )}

      {data?.splittable && !isLoading && families.length === 0 && (
        <div className="mt-3 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
          This customer has bought nothing in the lookback, so there is no list to offer. The request has to be
          answered by hand — or the customer question is answered wrongly and the amber customer panel is the
          place to fix it.
        </div>
      )}

      {families.length > 0 && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="quiet" size="sm" className="underline hover:no-underline" onClick={() => setPicked(new Set(available.map(f => f.productId)))}>
              Select all {available.length}
            </Button>
            <Button variant="quiet" size="sm" className="underline hover:no-underline" onClick={() => setPicked(new Set())}>
              Clear
            </Button>
          </div>

          <ul className="mt-2 max-h-96 space-y-1 overflow-y-auto">
            {families.map(f => (
              <li
                key={`${f.companyCode}-${f.productId}`}
                className="rounded border border-amber-200 bg-white px-3 py-2"
              >
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    disabled={pending || f.alreadySplitAs !== null || !data?.splittable}
                    checked={picked.has(f.productId)}
                    onChange={() => toggle(f.productId)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-gray-900">{f.displayName ?? f.productId}</span>
                    <span className="ml-2 font-mono text-xs text-muted">{f.productId}</span>
                    {!f.masterRowExists && (
                      <span
                        className="ml-2 text-xs text-muted"
                        title="This family has no master record in the ERP, so the name comes from one of its pack sizes. 42 % of dit families are like this — it is normal, not an error."
                      >
                        name from a variant
                      </span>
                    )}
                    <span className="mt-0.5 block text-xs text-gray-600">
                      {f.openOrderOnly ? 'Ordered, not yet delivered' : f.lastKind === 'Sample' ? 'Sample' : 'Invoiced'}
                      {f.lastDocumentDate ? ` · last ${f.lastDocumentDate}` : ''}
                      {f.lastDocumentCode ? ` · ${f.lastDocumentCode}` : ''}
                      {f.variantCount > 1 ? ` · ${f.variantCount} pack sizes` : ''}
                    </span>
                    {f.alreadySplitAs && (
                      <span className="mt-0.5 block text-xs text-gray-500">
                        Already split off as{' '}
                        <Link to={`/tickets/${f.alreadySplitAs.ticketNumber}`} className="text-info hover:underline">
                          {f.alreadySplitAs.ticketNumber}
                        </Link>{' '}
                        ({f.alreadySplitAs.status})
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {overLimit && data && (
            <div className="mt-2 text-xs text-red-700">
              {chosen.length} selected — the limit is {data.maxPerSplit} per pass. Each one is a ticket, a draft and
              a separate mail; split the rest afterwards.
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-amber-200 pt-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={pending || chosen.length === 0 || overLimit || !data?.splittable}
              disabledReason="Tick at least one family first."
              onClick={() => onSplit(chosen.map(f => f.productId), chosen)}
            >
              {pending ? 'Splitting…' : `Create ${chosen.length} ticket${chosen.length === 1 ? '' : 's'}`}
            </Button>
            <span className="text-xs text-amber-900/80">
              Nothing is sent. Each new ticket runs the purchase check by itself, and the agent writes its reply
              draft on the next poll — into Approvals, where somebody approves it.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * =========================================================================
 * SEVERAL ARTICLES IN ONE MAIL (P29, Roman's decision 2)
 * =========================================================================
 * "Please send the data sheets for A, B and C" is one mail and three answers.
 * Until this phase the panel could take exactly one of them, and the other two
 * lived in somebody's memory. Ticking several here means: the FIRST becomes
 * this ticket's article — same thread, same case file, same number — and each
 * further one becomes a ticket of its own with its own purchase check and its
 * own reply. That is N mails to the customer, so the count is said out loud
 * here and again in the confirmation.
 *
 * The multi-pick carries the SAME three things a single pick carries
 * (`FactChoice`: the candidates, the server's consequences, and whether this is
 * an edit) — because setting the first article on a ticket that already has one
 * withdraws a waiting draft, and the person must be told that before they
 * click, not after N tickets exist.
 */
export function SetProductPanel({
  ticketNumber, rawText, companyCode, mode = 'answer', currentName, pending, onChoose, onChooseMany, onCancel,
}: {
  ticketNumber: string;
  rawText: string | null;
  companyCode: string | null;
  mode?: FactPanelMode;
  currentName?: string | null;
  pending: boolean;
  onChoose: (choice: FactChoice<ProductCandidate>) => void;
  /** P29 — when given, the rows can be ticked and several go up at once. */
  onChooseMany?: (choice: FactChoice<ProductCandidate[]>) => void;
  onCancel?: () => void;
}) {
  const [term, setTerm] = useState('');
  /**
   * The picked candidates themselves, not their ids: a person searches "acti",
   * ticks one, searches "ruet", ticks another — and by then the first row is no
   * longer in `searchResults`. Keeping the objects is what lets the footer name
   * what has been picked so far.
   */
  const [picked, setPicked] = useState<ProductCandidate[]>([]);
  const { data, isLoading } = useProductCandidates(ticketNumber, term.trim(), true);

  const agent = data?.agentCandidates ?? [];
  const results = data?.searchResults ?? [];
  const suggestion = data?.suggestion ?? null;
  const consequences = data?.consequences;
  const editing = mode === 'edit';
  const multi = !!onChooseMany;
  const isEdit = data?.isEdit ?? editing;
  const emptyConsequences: FactEditConsequences = {
    drafts: [], authorizationRequest: null, sentReply: null, statusResetFrom: null,
  };
  const overLimit = picked.length > MAX_PICKED_PRODUCTS;

  const keyOf = (p: ProductCandidate) => `${p.companyCode}-${p.productId}`;
  const isPicked = (p: ProductCandidate) => picked.some(q => keyOf(q) === keyOf(p));
  const toggle = (p: ProductCandidate) => setPicked(prev => (
    prev.some(q => keyOf(q) === keyOf(p)) ? prev.filter(q => keyOf(q) !== keyOf(p)) : [...prev, p]
  ));

  // There is no company selector here on purpose: a product belongs to exactly
  // one company, and the CUSTOMER decides which. Offering the choice twice is
  // how a dit article lands on a dch customer's ticket.
  const pick = (p: ProductCandidate) => onChoose({
    candidate: p,
    consequences: consequences ?? emptyConsequences,
    isEdit,
  });
  const pickMany = () => onChooseMany?.({
    candidate: picked,
    consequences: consequences ?? emptyConsequences,
    isEdit,
  });

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold text-warn-ink">
          {editing ? 'Change the article' : 'Which article is this?'}
        </h2>
        {/* P39: 36 × 16 px before. The kit's quiet button, like the Cancel on the
            fact card it mirrors — same height, same focus ring, same 32 px floor. */}
        {onCancel && (
          <Button variant="quiet" size="sm" className="shrink-0 underline hover:no-underline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <p className="mt-1 text-amber-900/90">
        The mail said <strong>{rawText || 'nothing the agent could use'}</strong>. The purchase check that decides
        whether the document may go out is asked about THIS article, so it has to be the right one.
      </p>

      {editing && <CurrentValue label="Currently" value={currentName ?? null} raw={rawText} />}

      {consequenceSentence(consequences) && (
        <p className="mt-2 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
          If you change it: {consequenceSentence(consequences)}
        </p>
      )}

      {!companyCode ? (
        <div className="mt-3 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
          This ticket has no company yet, so there is nothing to search. Set the customer first — the company
          follows from the customer, and the same name means different articles in dit and dch.
        </div>
      ) : (
        <>
          <Input
            type="search"
            value={term}
            placeholder={rawText ? `Search ${companyCode} products — e.g. "${rawText}"` : `Search ${companyCode} products`}
            onChange={e => setTerm(e.target.value)}
            className="mt-3"
          />

          {agent.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium uppercase tracking-wide text-amber-900/70">
                What the agent considered
              </div>
              <CandidateListCaption
                meta={data?.agentList}
                explanation={data?.agentExplanation}
                ingest={data?.agentIngest}
              />
              <ul className="mt-1 space-y-1">
                {agent.map(p => (
                  <CandidateRow
                    key={`agent-${p.companyCode}-${p.productId}`}
                    name={p.productName}
                    id={p.productId}
                    companyCode={p.companyCode}
                    score={p.score}
                    band={p.band}
                    suggested={isSuggested(suggestion, p.productId, p.companyCode)}
                    disabled={pending || p.companyCode !== companyCode}
                    note={p.companyCode !== companyCode ? `belongs to ${p.companyCode}` : undefined}
                    selectable={multi && p.companyCode === companyCode}
                    selected={isPicked(p)}
                    onToggle={() => toggle(p)}
                    onPick={() => pick(p)}
                  />
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3">
            <div className="text-xs font-medium uppercase tracking-wide text-amber-900/70">Search</div>
            {isLoading && <div className="mt-1 text-xs text-amber-900/60">Searching…</div>}
            {!isLoading && results.length === 0 && (
              <div className="mt-1 text-xs text-amber-900/60">
                No {companyCode} product is even close. The match ignores spaces, dashes and the ***
                discontinued marker and tolerates a typo, so &quot;Ruetasolv DI&quot; finds both
                &quot;RUETASOLV DI***&quot; and &quot;RUETASOLV-DI&quot;, and so does &quot;Ruetaslov DI&quot;.
                A number is part of the name: &quot;Acticide 45&quot; is never preselected for
                &quot;Acticide 46&quot;.
              </div>
            )}
            <ul className="mt-1 space-y-1">
              {results.map(p => (
                <CandidateRow
                  key={`search-${p.companyCode}-${p.productId}`}
                  name={p.productName}
                  id={p.productId}
                  companyCode={p.companyCode}
                  score={p.score}
                  band={p.band}
                  suggested={isSuggested(suggestion, p.productId, p.companyCode)}
                  disabled={pending}
                  note={[
                    p.via === 'Fuzzy' ? 'close, not exact' : null,
                    p.variantCount && p.variantCount > 1 ? `${p.variantCount} packaging variants` : null,
                  ].filter(Boolean).join(' · ') || undefined}
                  selectable={multi}
                  selected={isPicked(p)}
                  onToggle={() => toggle(p)}
                  onPick={() => pick(p)}
                />
              ))}
            </ul>
          </div>

          {/* P29. The footer appears only once something is ticked: a control
              offering to create tickets is not something to have on screen while
              somebody is still reading the list. */}
          {multi && picked.length > 0 && (
            <div className="mt-3 border-t border-amber-200 pt-3">
              <div className="text-xs text-amber-900/80">
                Picked: {picked.map(p => p.productName || p.productId).join(', ')}
              </div>
              {overLimit && (
                <div className="mt-1 text-xs text-red-700">
                  {picked.length} picked — the limit is {MAX_PICKED_PRODUCTS} per pass. Each one is a ticket, a
                  draft and a separate mail; pick the rest afterwards.
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pending || overLimit}
                  onClick={pickMany}
                >
                  {picked.length === 1
                    ? `Set ${picked[0].productName || picked[0].productId}`
                    : `Set 1 · create ${picked.length - 1} ticket${picked.length - 1 === 1 ? '' : 's'}`}
                </Button>
                <Button variant="quiet" size="sm" className="underline hover:no-underline" onClick={() => setPicked([])}>
                  Clear
                </Button>
                <span className="text-xs text-amber-900/80">
                  {picked.length === 1
                    ? 'One article: this is the ordinary "set the article".'
                    : `${picked[0].productName || picked[0].productId} stays on ${ticketNumber}; the other `
                      + `${picked.length - 1} become tickets of their own, on the same mail thread. Each is `
                      + 'answered with its own mail.'}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Name big, id small — the P11 display rule, in the one place a choice is made.
 *
 * `suggested` is the SERVER's answer to "may this row be preselected", not a
 * threshold this component applies. It changes two things and nothing else: the
 * row is ringed, and the button says CONFIRM instead of "Use this" — because the
 * one thing a person must not come away believing is that the agent already set
 * it. It did not, and a fuzzy match never will (Roman, decision 2).
 */
export function CandidateRow({
  name, id, companyCode, disabled, note, score, band, suggested, selectable, selected, onToggle, onPick,
}: {
  name: string;
  id: string;
  companyCode: string;
  disabled: boolean;
  note?: string;
  score?: number | null;
  band?: MatchBand | null;
  suggested?: boolean;
  /** P29 — the row can be ticked as one of several. The button stays: one click is still one article. */
  selectable?: boolean;
  selected?: boolean;
  onToggle?: () => void;
  onPick: () => void;
}) {
  return (
    <li
      className={`flex items-center justify-between gap-3 rounded border bg-white px-3 py-1.5 ${
        selected ? 'border-amber-500 ring-1 ring-amber-500/40'
          : suggested ? 'border-green-500 ring-1 ring-green-500/40' : 'border-amber-200'
      }`}
    >
      {selectable && (
        <input
          type="checkbox"
          className="shrink-0"
          aria-label={`Pick ${name || id} as well`}
          disabled={disabled}
          checked={!!selected}
          onChange={() => onToggle?.()}
        />
      )}
      <div className="min-w-0">
        <div className="flex items-baseline">
          <span className="truncate font-medium text-gray-900">{name || id}</span>
          <MatchScoreBadge score={score ?? null} band={band ?? null} />
        </div>
        <div className="text-xs text-gray-500">
          <span className="rounded bg-gray-100 px-1 py-0.5 font-medium text-gray-700">{companyCode}</span>
          <span className="ml-1.5 font-mono">{id}</span>
          {note && <span className="ml-1.5 italic">· {note}</span>}
          {suggested && <span className="ml-1.5 font-medium text-brand-700">· suggested, not set</span>}
        </div>
      </div>
      {/* P39. This measured 24 px — §4's floor is 32 — and it was the last pair
          of hand-rolled fills on the screen: a green one for the suggested row, an
          amber one for the rest, in two colours the kit does not have.

          Both are SECONDARY now, and the difference stays exactly where P25 put
          it: the row's ring, the words "suggested, not set", and the button's own
          label. A green fill would have pulled towards the click, which is the
          opposite of what a suggestion is for — "a fuzzy hit never resolves". */}
      <Button
        variant="secondary"
        size="sm"
        className="shrink-0"
        disabled={disabled}
        onClick={onPick}
      >
        {suggested ? 'Confirm' : 'Use this'}
      </Button>
    </li>
  );
}

/**
 * =========================================================================
 * WHAT WAS ACTUALLY ASKED FOR (P28) — the classifier's own facts
 * =========================================================================
 * The customer and the article got a way out of review in P21. Everything the
 * model read BESIDE them had none. Measured on 2026-09-02: `DocTypeAmbiguous`
 * on four tickets, `RelayOriginUnclear` on four, `LanguageUnsupported` on two —
 * ten tickets for which `ReviewReasonGuide` said "A person decides this one"
 * and the only exit was the status dropdown, which wipes EVERY reason on the
 * ticket including the unanswered ones.
 *
 * Four controls, and they are deliberately not equal:
 *
 *   Language           writes `Tickets.RequestedLanguage`. It decides the
 *                      document that is searched for (C9) and the template the
 *                      covering mail is written from (P23), and it is part of
 *                      the dedupe key — so changing it re-runs the chain.
 *                      "Not stated" is a real value, not a blank: 25 of 47 live
 *                      tickets carry it, and it is what makes the destination
 *                      market decide instead.
 *   Document type      writes nothing. `RequestedDocTypeCode` is already `SDS`
 *                      on 51 of 51 tickets; what is open is whether anybody
 *                      BELIEVES it. Confirming answers the question and puts a
 *                      name in the ledger.
 *   Reply to sender    writes nothing, and deliberately does not change who
 *                      receives the reply — that is `origin.ts`, untouched by
 *                      Roman's P20 decision. It answers "we could not establish
 *                      whose request this forward is".
 *   Priority / due     order a queue for people. Nothing in the pipeline reads
 *                      them, so nothing is re-run and no draft is withdrawn.
 *
 * The panel says which is which, because "this will rewrite the reply" and
 * "this moves a ticket up a list" are not the same click.
 */
export const REQUEST_LANGUAGES = ['DE', 'EN', 'FR', 'IT', 'ES'];

/** The five reasons this panel answers. Mirrors the three lists in `set-request.ts`. */
export const REQUEST_FACT_REASONS = [
  'LowConfidence:language', 'LanguageUnsupported',
  'DocTypeAmbiguous', 'LowConfidence:docType',
  'RelayOriginUnclear',
];

export interface RequestFactsEdit {
  requestedLanguage?: string | null;
  docTypeConfirmed?: true;
  relayOriginConfirmed?: true;
  priority?: string;
  dueAt?: string | null;
}

export function RequestFactsPanel({
  ticket, reasons, consequences, pending, onSubmit, onCancel,
}: {
  ticket: Ticket;
  reasons: string[];
  /** What a chain-relevant change would withdraw; absent when nothing is known yet. */
  consequences?: FactEditConsequences;
  pending: boolean;
  onSubmit: (edit: RequestFactsEdit, rerunsChain: boolean) => void;
  onCancel?: () => void;
}) {
  const [language, setLanguage] = useState(ticket.requestedLanguage ?? '');
  const [priority, setPriority] = useState(ticket.priority);
  const [dueAt, setDueAt] = useState(ticket.dueAt ? ticket.dueAt.slice(0, 10) : '');

  const docTypeOpen = reasons.includes('DocTypeAmbiguous') || reasons.includes('LowConfidence:docType');
  const relayOpen = reasons.includes('RelayOriginUnclear');

  const languageChanged = (ticket.requestedLanguage ?? '') !== language;
  const priorityChanged = ticket.priority !== priority;
  const currentDue = ticket.dueAt ? ticket.dueAt.slice(0, 10) : '';
  const dueChanged = currentDue !== dueAt;
  const anyChange = languageChanged || priorityChanged || dueChanged;

  function save() {
    const edit: RequestFactsEdit = {};
    if (languageChanged) edit.requestedLanguage = language === '' ? null : language;
    if (priorityChanged) edit.priority = priority;
    if (dueChanged) edit.dueAt = dueAt === '' ? null : new Date(`${dueAt}T00:00:00Z`).toISOString();
    onSubmit(edit, languageChanged);
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold text-warn-ink">What was asked for?</h2>
        {/* P39: 36 × 16 px before. The kit's quiet button, like the Cancel on the
            fact card it mirrors — same height, same focus ring, same 32 px floor. */}
        {onCancel && (
          <Button variant="quiet" size="sm" className="shrink-0 underline hover:no-underline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
      <p className="mt-1 text-amber-900/90">
        The document type, the language and whose request this is were read out of the mail by the agent. Correct
        any of them here — the first two decide which document is looked for and which wording the reply is written
        from, so changing them runs the chain again.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs text-amber-900/70" htmlFor="request-language">
            Requested language
          </label>
          <Select
            id="request-language"
            value={language}
            onChange={e => setLanguage(e.target.value)}
            className="mt-1"
          >
            {/* An empty option is not "unset the field", it is the FACT that the
                mail named no language — which is what lets the destination
                market decide (C9 level 2). */}
            <option value="">Not stated in the mail</option>
            {REQUEST_LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
          </Select>
        </div>
        <div>
          <label className="block text-xs text-amber-900/70" htmlFor="request-priority">Priority</label>
          <Select
            id="request-priority"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            className="mt-1"
          >
            {['Low', 'Normal', 'High'].map(v => <option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
        <div>
          <label className="block text-xs text-amber-900/70" htmlFor="request-due">Due date</label>
          <Input
            id="request-due"
            type="date"
            value={dueAt}
            onChange={e => setDueAt(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="text-xs text-amber-900/70">
          <span className="block">Document type</span>
          <span className="mt-1 block text-sm text-gray-900">{ticket.requestedDocTypeCode}</span>
          <span className="mt-1 block">
            The pilot answers safety data sheets only, so this is not a list — it is a question about whether the
            mail asked for one.
          </span>
        </div>
      </div>

      {languageChanged && consequenceSentence(consequences) && (
        <p className="mt-3 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-amber-900">
          Changing the language: {consequenceSentence(consequences)}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-amber-200 pt-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={pending || !anyChange}
          disabledReason="Change something first — nothing here differs from what the ticket already says."
          onClick={save}
        >
          {pending ? 'Saving…' : 'Save'}
        </Button>
        <span className="text-xs text-amber-900/80">
          {languageChanged
            ? 'The language changed, so the document search and the reply are run again. Nothing is sent.'
            : anyChange
              ? 'Priority and due date order the queue for people; nothing is re-run and no draft is touched.'
              : 'Nothing changed yet.'}
        </span>
      </div>

      {(docTypeOpen || relayOpen) && (
        <div className="mt-3 space-y-2 border-t border-amber-200 pt-3">
          {docTypeOpen && (
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="secondary" size="sm" disabled={pending} onClick={() => onSubmit({ docTypeConfirmed: true }, true)}>
                Yes, this is an SDS request
              </Button>
              <span className="text-xs text-amber-900/80">
                Clears the document-type question and re-runs the chain. Your name is recorded against it.
              </span>
            </div>
          )}
          {relayOpen && (
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="secondary" size="sm" disabled={pending} onClick={() => onSubmit({ relayOriginConfirmed: true }, true)}>
                Reply to the resolved sender
              </Button>
              <span className="text-xs text-amber-900/80">
                Confirms whose request this forwarded mail is. It does NOT change the reply address — that is
                decided from the mail itself and stays as it is.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ONE REQUEST, TWO TICKETS (Roman, decision 3 — P21 held it, P27 decides it).
 *
 * This panel is shown on BOTH sides of the pair and links both ways, which is
 * the whole point: a banner only on the held ticket is a banner on the ticket
 * nobody opens. Whoever picks up the older one has to see that a second request
 * is waiting on their answer.
 *
 * It renders three different situations and never blurs them:
 *
 *   - HELD    — the agent stopped this ticket (`PotentialDuplicate`). Nothing
 *               was authorised, nothing was drafted.
 *   - FOUND   — nothing was stopped. Two tickets cover the same request and
 *               nobody has decided anything: the same-tick race two senders can
 *               produce, or a fact corrected after ingest. `linked` is the
 *               server&apos;s word for which of the two this is.
 *   - WAITING — the other direction: something is held against THIS ticket.
 *
 * The actions never take a target. The server resolves which ticket this one
 * duplicates, from the recorded link or from the same key the pipeline dedupes
 * on — the P24 attachment rule applied to a close.
 */
export function DuplicatePanel({
  ticket, held, canEdit, merging, releasing, onMerge, onNotDuplicate,
}: {
  ticket: Ticket;
  /**
   * Whether the ticket carries `PotentialDuplicate` RIGHT NOW — which is a
   * different question from whether a link is stored. A merged ticket keeps its
   * `DuplicateOfTicketId` forever and is not held by anything; a raced pair is
   * not held either. Only this says "nothing ran for this ticket".
   */
  held: boolean;
  canEdit: boolean;
  merging: boolean;
  releasing: boolean;
  onMerge: () => void;
  onNotDuplicate: () => void;
}) {
  const other = ticket.duplicateOf ?? null;
  const waiting = ticket.duplicates ?? [];
  if (!other && waiting.length === 0) return null;

  const merged = ticket.status === 'Closed' && ticket.closeReason === 'Duplicate';
  const kind = !other ? 'duplicateOfThis' : merged ? 'mergedInto' : held ? 'duplicateHeld' : 'duplicateFound';
  // A closed ticket is not decided again, and neither is one that is not held
  // and has no open partner to be confused with.
  const decidable = canEdit && !!other && !merged;

  return (
    <div className="rounded-lg border border-blue-300 bg-blue-50 p-4 text-sm">
      <h2 className="text-sm font-semibold text-info" title={tooltipFor(DUPLICATE_LABELS, kind)}>
        {labelFor(DUPLICATE_LABELS, kind)}
      </h2>

      {other && (
        <>
          <p className="mt-1 text-blue-900/90">
            {merged
              ? 'This ticket was closed as a duplicate. Its mail sits in the other ticket’s case file and the '
                + 'customer is answered from there.'
              : held
                ? 'The same customer, article, document type and language are already covered by an open ticket, so '
                  + 'this one is held here: no purchase check was run and no reply was drafted for it. Two replies to '
                  + 'one request is how a customer gets the same document twice.'
                : 'An open ticket covers the same customer, article, document type and language. Nothing was held — '
                  + 'the two mails arrived too close together for the agent to see the pair, or a fact was corrected '
                  + 'afterwards. Both tickets are live work until somebody decides.'}
          </p>
          <p className="mt-2 text-blue-900">
            {merged ? 'Merged into' : held ? 'Probably the same request as' : 'The other ticket is'}{' '}
            <Link to={`/tickets/${other.ticketNumber}`} className="font-medium underline">
              {other.ticketNumber}
            </Link>{' '}
            <span className="text-xs text-blue-900/70">
              ({labelFor(TICKET_STATUS_LABELS, other.status)})
            </span>
          </p>

          {decidable && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm"
                type="button"
                disabled={merging || releasing}
                title={DUPLICATE_ACTIONS.merge.tooltip}
                onClick={onMerge}
              >
                {merging ? 'Merging…' : `${DUPLICATE_ACTIONS.merge.label} (${other.ticketNumber})`}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={merging || releasing}
                title={DUPLICATE_ACTIONS.notDuplicate.tooltip}
                onClick={onNotDuplicate}
              >
                {releasing ? 'Releasing…' : DUPLICATE_ACTIONS.notDuplicate.label}
              </Button>
            </div>
          )}
        </>
      )}

      {waiting.length > 0 && (
        <p className={`text-blue-900/90 ${other ? 'mt-3 border-t border-blue-200 pt-2' : 'mt-1'}`}>
          <span title={tooltipFor(DUPLICATE_LABELS, 'duplicateOfThis')}>
            {waiting.length === 1 ? 'One later request' : `${waiting.length} later requests`} for the same thing{' '}
            {waiting.length === 1 ? 'is' : 'are'} held naming this ticket:
          </span>{' '}
          {waiting.map((w, i) => (
            <span key={w.ticketNumber}>
              {i > 0 && ', '}
              <Link to={`/tickets/${w.ticketNumber}`} className="font-medium underline">{w.ticketNumber}</Link>
            </span>
          ))}
          . Answering this one does not answer {waiting.length === 1 ? 'it' : 'them'} — each is decided on its own
          screen.
        </p>
      )}
    </div>
  );
}

/**
 * Same customer, same article — a DIFFERENT language, so a different document
 * and a different reply (Roman&apos;s decision 3 keeps the language in the key).
 *
 * Never a hold and never a review reason. It is a pointer, so that whoever
 * answers the German request knows the Italian one is open too.
 */
/**
 * ONE MAIL, SEVERAL TICKETS (P29/P26) — shown from both ends.
 *
 * A sibling ticket is not a duplicate and must not read like one: nothing is
 * held, nothing is waiting on anybody's decision, and each ticket is answered
 * with its own mail. What a person needs is the ability to get from any one of
 * them to the others — the parent to see what it became, a child to see where
 * it came from and what else came out of the same request.
 *
 * The relation is the stored `SplitFromTicketId` and is never re-derived here
 * (`siblings.ts`): unlike a duplicate, a split records a decision somebody made.
 */
export function SplitRelationsPanel({ ticket }: { ticket: Ticket }) {
  const parent = ticket.splitFrom ?? null;
  const children = ticket.splitInto ?? [];
  if (!parent && children.length === 0) return null;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm">
      <h2 className="text-sm font-semibold text-info">One request, several tickets</h2>
      {parent && (
        <p className="mt-1 text-blue-900/90">
          This ticket was raised from{' '}
          <Link to={`/tickets/${parent.ticketNumber}`} className="font-medium text-info hover:underline">
            {parent.ticketNumber}
          </Link>{' '}
          ({labelFor(TICKET_STATUS_LABELS, parent.status)}) — one mail that named more than one article. Each
          ticket carries one article, runs its own purchase check and is answered with its own mail.
        </p>
      )}
      {children.length > 0 && (
        <>
          <p className="mt-1 text-blue-900/90">
            {children.length} other ticket{children.length === 1 ? '' : 's'} came out of this same request. They
            are separate answers, not duplicates: nothing here is waiting on them.
          </p>
          <ul className="mt-2 space-y-1">
            {children.map(c => (
              <li key={c.ticketNumber} className="rounded border border-blue-200 bg-white px-3 py-1.5">
                <Link to={`/tickets/${c.ticketNumber}`} className="font-medium text-info hover:underline">
                  {c.ticketNumber}
                </Link>
                <span className="ml-2 text-xs text-gray-600">
                  {labelFor(TICKET_STATUS_LABELS, c.status)}
                </span>
                {c.productId && <span className="ml-2 font-mono text-xs text-muted">{c.productId}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function RelatedLanguagePanel({ ticket }: { ticket: Ticket }) {
  const related = ticket.relatedOtherLanguage ?? [];
  if (related.length === 0) return null;
  return (
    <div className="rounded-card border border-hair bg-ground p-3 text-sm">
      {/* A real heading like every other card on the page (S2-12), although the
          card is one sentence long: a screen reader offers no way past a block
          that has none, and this one sits between the delivery proof and the
          correction. */}
      <h2 className="inline text-sm font-semibold text-ink" title={tooltipFor(DUPLICATE_LABELS, 'relatedOtherLanguage')}>
        {labelFor(DUPLICATE_LABELS, 'relatedOtherLanguage')}
      </h2>
      <span className="text-muted">
        {' '}— the same article for the same customer, asked for in another language:{' '}
        {related.map((r, i) => (
          <span key={r.ticketNumber}>
            {i > 0 && ', '}
            <Link to={`/tickets/${r.ticketNumber}`} className="text-info underline">{r.ticketNumber}</Link>
            {r.requestedLanguage ? ` (${r.requestedLanguage})` : ''}
          </span>
        ))}
        . Not a duplicate: it needs its own document and its own reply.
      </span>
    </div>
  );
}

/**
 * =========================================================================
 * ONE PANEL PER OPEN QUESTION (P22, masterplan task 4)
 * =========================================================================
 * A review reason used to be a raw code in grey text — `AuthorizationRequestExpired`
 * — with no indication of what to do about it. Six live tickets have sat in
 * exactly that state since the pilot began, not because the answer is hard but
 * because nothing said there was an answer.
 *
 * This lists every open reason on the ticket with three things: what it means,
 * what happens next, and WHERE the control is. The controls themselves are not
 * duplicated here — the customer panel, the product panel and the red C9 panel
 * are elsewhere on this screen and stay the single place each decision is made.
 * What this adds is the pointer, plus the one action that had no home at all:
 * asking the authorization question again.
 */
export function ReviewReasonGuide({
  reasons, onRecompute, recomputePending, canEdit, requestedLanguage, docTypeCode, onEditRequest,
}: {
  reasons: string[];
  onRecompute: () => void;
  recomputePending: boolean;
  canEdit: boolean;
  requestedLanguage: string | null;
  docTypeCode: string;
  /** P28 — opens the request panel for the five question-only reasons. */
  onEditRequest?: () => void;
}) {
  if (reasons.length === 0) return null;

  const askAgain = reasons.some(r => ASK_AGAIN_REASONS.includes(r));
  // P28. The five reasons whose control is the request panel. The pointer under
  // each reason names it; this is the button, so the index is not a dead end for
  // exactly the reasons that had no control at all before this phase.
  const editRequest = reasons.some(r => REQUEST_FACT_REASONS.includes(r));

  return (
    <Card title="What is being asked" className="text-sm">
      <ul className="space-y-2">
        {reasons.map(r => (
          <li key={r} className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-gray-900">{labelFor(REVIEW_REASON_LABELS, r)}</span>
              <span className="font-mono text-xs text-muted">{r}</span>
            </div>
            <p className="mt-0.5 text-xs text-gray-600">{tooltipFor(REVIEW_REASON_LABELS, r)}</p>
            <ReasonAction reason={r} requestedLanguage={requestedLanguage} docTypeCode={docTypeCode} />
          </li>
        ))}
      </ul>

      {editRequest && canEdit && onEditRequest && (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
          <Button variant="secondary" size="sm" onClick={onEditRequest}>
            What was asked for?
          </Button>
          <span className="text-xs text-gray-500">
            The document type, the language and whose request this is. Answering any of them clears its question
            and runs the document search and the reply again; nothing is sent.
          </span>
        </div>
      )}

      {askAgain && canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
          <Button variant="secondary" size="sm"
            disabled={recomputePending}
            onClick={onRecompute}
          >
            {recomputePending ? 'Running…' : 'Ask again'}
          </Button>
          <span className="text-xs text-gray-500">
            Runs the purchase check again on the facts already on this ticket. If it still cannot decide, a fresh
            request goes to the responsible Sales Manager — the expired one cannot be revived. A reply draft is
            written if everything comes out settled; nothing is sent without an approval.
          </span>
        </div>
      )}
    </Card>
  );
}

/**
 * The reasons for which "run it again" is the actual move — the authorization
 * question is open and only step 7 can close it. Deliberately NOT
 * `AuthorizationRejectedBySales`: Sales answered that one, and offering a
 * button that quietly re-asks a decided question is how a "no" gets lost.
 */
export const ASK_AGAIN_REASONS = [
  'AuthorizationRequestExpired',
  'AuthSalesManagerUnresolved',
  'AuthRouteToRegulatory',
  'AuthCustomerUnresolved',
  'AuthProductUnresolved',
];

/**
 * "We have no wording to write to this person in."
 *
 * =========================================================================
 * WHY THIS IS NOT THE RED C9 PANEL
 * =========================================================================
 * `LanguageOverridePanel` (red, on `TicketDetail`) asks Regulatory to send a
 * customer a SAFETY DATA SHEET in a language they may not legally be able to
 * read. This one asks whether the covering NOTE may be written in English
 * because Katia has not written the Italian one yet. Same word — "language" —
 * two decisions of very different weight, so they get different colours,
 * different buttons, different audit actions, and are never signed by one click.
 *
 * Amber rather than red on purpose: this is the AGREED way through until the
 * wording exists (Roman, decision 1, 2026-09-01), not an exception to a rule.
 * The live template state is the reason it exists at all — `sds_ack_delivery`
 * is written in EN/DE/IT and everything else is English only, so under the new
 * rule every refusal in DE/FR/IT/ES stops here.
 *
 * It states WHY the rule arrived at the language it did, because the fix
 * differs: "the customer asked for IT" is answered by writing the Italian text,
 * while "we could not tell" is usually answered by setting the customer.
 */
export function ReplyLanguagePanel({
  ticket, reasons, pending, onOverride,
}: {
  ticket: Ticket;
  reasons: string[];
  pending: boolean;
  onOverride: () => void;
}) {
  const undetermined = reasons.includes('ReplyLanguageUnavailable');
  const owed = ticket.requestedLanguage
    ?? (undetermined ? null : ticket.messageLanguage);

  return (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <h2 className="text-sm font-semibold text-warn-ink">
        {undetermined ? 'No language to write the reply in' : `No approved wording in ${owed ?? 'that language'}`}
      </h2>

      <p className="mt-1 text-amber-900/90">
        {undetermined ? (
          <>
            The mail named no language, nothing usable could be read from the language it was written in, and the
            customer&rsquo;s country has no single market language. The agent does not pick a language nobody chose.
          </>
        ) : (
          <>
            The reply should be written in <strong>{owed ?? 'the language the rule arrived at'}</strong>, and there is
            no active message template for this document type in it. The agent no longer substitutes English on its own.
          </>
        )}
      </p>

      <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-0.5 text-xs text-amber-900/80 sm:grid-cols-2">
        <div>
          <dt className="inline text-amber-900/60">Asked for: </dt>
          <dd className="inline">{ticket.requestedLanguage ?? 'nothing — the mail did not say'}</dd>
        </div>
        <div>
          <dt className="inline text-amber-900/60">Written in: </dt>
          <dd className="inline">
            {/* P39, S2-15. The audit found the raw decimal on the facts card;
                this is the SECOND site, and it read "DE (confidence 0.95)" on the
                one panel whose whole subject is which language to answer in. */}
            {ticket.messageLanguage
              ? (
                <span className="inline-flex flex-wrap items-center gap-x-1.5">
                  {ticket.messageLanguage}
                  <Confidence value={ticket.messageLanguageConfidence} about="which language this mail is written in" />
                </span>
              )
              : 'not recorded'}
          </dd>
        </div>
        <div>
          <dt className="inline text-amber-900/60">Customer: </dt>
          <dd className="inline">{ticket.customerName ?? 'not settled'}</dd>
        </div>
        <div>
          <dt className="inline text-amber-900/60">Document type: </dt>
          <dd className="inline">{ticket.requestedDocTypeCode}</dd>
        </div>
      </dl>

      <p className="mt-2 text-xs text-amber-900/80">
        The way out that costs nothing later:{' '}
        <Link
          to={`/masters?tab=templates&language=${owed ?? ''}&docType=${ticket.requestedDocTypeCode}`}
          className="font-medium text-amber-900 underline"
        >
          write and activate the wording under Masters
        </Link>
        . Activating a template picks the waiting tickets up on its own &mdash; you do not have to come back here.
      </p>

      {/* Secondary, not a fill (P39). It is a signed decision — the ConfirmModal
          carries that — but it is deliberately the LESSER of the two language
          overrides: P23 kept this one apart from the red C9 button because
          writing the covering note in English is not the same as sending a data
          sheet in a language the customer cannot read. Two identical-looking red
          buttons would have said it was. */}
      <div className="mt-3">
        <Button variant="secondary" size="sm" disabled={pending} onClick={onOverride}>
          {pending ? 'Composing…' : 'Answer in English instead…'}
        </Button>
      </div>
    </div>
  );
}

/** Where the control for this reason actually is. */
function ReasonAction({ reason, requestedLanguage, docTypeCode }: { reason: string; requestedLanguage: string | null; docTypeCode: string }) {
  const family = familyOfReason(reason);

  // P37a. The only DERIVED reason in the list, and the only one whose control is
  // on another screen: this ticket carries a reply somebody already approved and
  // the recipient policy held. No approve button here (decision K2) — the Reply
  // card above links to the draft, which is where the address, the wording and
  // the document can be changed.
  if (reason === 'ApprovedNotSent') {
    return (
      <Pointer>
        Nothing failed and nothing was sent. Use the <strong>Reply</strong> card above to open the draft in
        Approvals — from there it can be returned to the queue, re-addressed, or left until the pilot&rsquo;s
        allowlist widens. This line disappears by itself the moment the mail goes out or the draft is replaced.
      </Pointer>
    );
  }

  if (family === 'customer') {
    return <Pointer>Use the amber <strong>“Which customer is this?”</strong> panel below. Setting it re-runs the whole chain.</Pointer>;
  }
  if (reason === 'ProductScopeAllPurchased') {
    return (
      <Pointer>
        Use the amber <strong>“Which of their products?”</strong> panel below. It lists what this customer has
        actually bought; every family you tick becomes its own ticket and runs on by itself.
      </Pointer>
    );
  }
  if (family === 'product') {
    return <Pointer>Use the amber <strong>“Which article is this?”</strong> panel below.</Pointer>;
  }
  if (reason === 'DraftDocumentLanguageUnavailable') {
    return <Pointer>Either file the missing {requestedLanguage ?? 'requested'} version in the official archive and press <strong>Compose again</strong>, or use the red override panel below — it records your name against that decision.</Pointer>;
  }
  if (reason === 'DraftNoTemplate') {
    return (
      <Pointer>
        Write and activate the wording under{' '}
        <Link to={`/masters?tab=templates&language=${requestedLanguage ?? ''}&docType=${docTypeCode}`} className="text-info hover:underline">
          Masters &rarr; message templates
        </Link>
        {' '}&mdash; activating it picks this ticket up on its own, no button needed. Or use the amber panel below to
        answer in English, with your name against that decision.
      </Pointer>
    );
  }
  if (reason === 'ReplyLanguageUnavailable') {
    return (
      <Pointer>
        Nobody said which language to write in. If the customer is missing or wrong, the amber
        {' '}<strong>&ldquo;Which customer is this?&rdquo;</strong> panel usually answers it &mdash; the country decides the
        language. Otherwise use the amber language panel below to answer in English.
      </Pointer>
    );
  }
  if (reason === 'DraftPlaceholderUnfilled') {
    return (
      <Pointer>
        Fix the wording under{' '}
        <Link to={`/masters?tab=templates&language=${requestedLanguage ?? ''}&docType=${docTypeCode}`} className="text-info hover:underline">
          Masters &rarr; message templates
        </Link>
        , then press <strong>Compose again</strong>.
      </Pointer>
    );
  }
  if (reason === 'AuthorizationRejectedBySales') {
    return (
      <Pointer>
        Sales has answered. The refusal reply, if one was written, is in{' '}
        <Link to="/approvals" className="text-info hover:underline">Approvals</Link>. Nothing clears this reason
        automatically — that is deliberate.
      </Pointer>
    );
  }
  if (reason === 'AuthorizationRequestExpired' || reason === 'AuthSalesManagerUnresolved' || reason === 'AuthRouteToRegulatory') {
    return (
      <Pointer>
        Press <strong>Ask again</strong> below to re-run the check and send a fresh request. Decisions are made on{' '}
        <Link to="/authorizations" className="text-info hover:underline">Authorizations</Link>.
      </Pointer>
    );
  }
  if (reason === 'PotentialDuplicate') {
    return (
      <Pointer>
        See the blue panel below: it names the other ticket and offers the two decisions —{' '}
        <strong>{DUPLICATE_ACTIONS.merge.label}</strong> or <strong>{DUPLICATE_ACTIONS.notDuplicate.label}</strong>.
      </Pointer>
    );
  }
  if (reason === 'DocumentNotFound') {
    return <Pointer>File the document in the official archive (<span className="font-mono text-xs">ORG-Regulatory &gt; General – Compliance Docs</span>), then press <strong>Compose again</strong>.</Pointer>;
  }
  // P28 — the five questions that had no control at all until this phase. Each
  // now points at the SAME panel, because they are one form: what the mail
  // actually asked for.
  if (reason === 'DocTypeAmbiguous' || reason === 'LowConfidence:docType') {
    return (
      <Pointer>
        Open <strong>&ldquo;What was asked for?&rdquo;</strong> on the header card and press{' '}
        <strong>&ldquo;Yes, this is an SDS request&rdquo;</strong>. That clears this question and runs the chain
        again; the pilot answers safety data sheets only, so a request for anything else is closed instead.
      </Pointer>
    );
  }
  if (reason === 'RelayOriginUnclear') {
    return (
      <Pointer>
        Open <strong>&ldquo;What was asked for?&rdquo;</strong> and press{' '}
        <strong>&ldquo;Reply to the resolved sender&rdquo;</strong>. It confirms whose request the forwarded mail
        is — it does not change the reply address, which is decided from the mail itself.
      </Pointer>
    );
  }
  if (reason === 'LanguageUnsupported' || reason === 'LowConfidence:language') {
    return (
      <Pointer>
        Set the language under <strong>&ldquo;What was asked for?&rdquo;</strong> on the header card — one of
        {' '}{REQUEST_LANGUAGES.join(', ')}, or <strong>&ldquo;Not stated in the mail&rdquo;</strong>, which lets the
        customer&rsquo;s own market decide. Saving it runs the document search and the reply again.
      </Pointer>
    );
  }
  return <Pointer>A person decides this one. Record what you decided as a note on the ticket before you change its status.</Pointer>;
}

function Pointer({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-xs text-gray-700">{children}</p>;
}
